import { GetterTree, ActionTree, MutationTree, Module } from 'vuex';
import PullAPIHelper from '@/helpers/pull-api-helper';
import { UrlMetadataResponse } from '@/interfaces/responses/url-metadata';
import { PullNotification } from '@/interfaces/responses/pull-api';
import { SurveyObject, FrequencyCappingType } from '@/interfaces/responses/survey';

// STATE
const state = () => ({
	pulls: [] as PullNotification[],
});

type State = ReturnType<typeof state>;

// GETTERS
const getters: GetterTree<State, any> = {
	surveys: (state, getters, rootState, rootGetters) => {
		const pulledSurveys = state.pulls.filter((pull: PullNotification) => pull.event.type === 'survey');

		let parsedSurveys: SurveyObject[] = [];
		for (const pull of pulledSurveys) {
			try {
				const survey: SurveyObject = JSON.parse(pull.template);
				survey.tagId = pull.tag_id;
				parsedSurveys.push(survey);
			} catch (err) {
				const locale = rootGetters['language/locale'];
				const language = rootGetters['language/language'];

				console.error(
					`[pull.store] getter/survery ${pull?.tag_id}, locale: ${locale}, lang: ${language}, template: ${pull.template}`
				);
				console.error(`[pull.store] getter/survery`, err);
			}
		}

		return parsedSurveys;
	},
	_surveys: state => {
		return state.pulls
			.filter((pull: PullNotification) => pull.event.type === 'survey')
			.map(
				(pull: PullNotification): SurveyObject => {
					const survey: SurveyObject = JSON.parse(pull.template);
					survey.tagId = pull.tag_id;
					return survey;
				}
			);
	},
	// current survey
	survey: (state, getters, rootState, rootGetters): SurveyObject | undefined => {
		if (!getters.surveys || !getters.surveys.length || getters.surveys.length === 0) {
			return undefined;
		}

		// handle frequency capping
		return getters.surveys.filter((survey: SurveyObject) => {
			const seenSurveyKeys = Object.keys(rootState.user.seenSurveys);
			switch (survey.frequency_capping_type) {
				// PER USER
				case FrequencyCappingType.PER_USER: {
					return (seenSurveyKeys as string[]).indexOf(survey.id + '') === -1;
				}
				// PER TITLE
				case FrequencyCappingType.PER_TITLE: {
					const titleId = [
						rootGetters['routing/urlMetadata'].object_type,
						'-',
						rootGetters['routing/urlMetadata'].object_id,
					].join('');
					return (
						seenSurveyKeys
							.map(surveyId => rootState.user.seenSurveys[surveyId])
							// flatten
							.reduce((root, item) => {
								root = root.concat(item);
								return root;
							}, [])
							.indexOf(titleId) === -1
					);
				}
			}
			return true;
		})[0];
		// @todo decide which survey to take if multiple are present
	},
};

// ACTIONS
const actions: ActionTree<State, any> = {
	loadPulls: async ({ commit, rootState, rootGetters }) => {
		const urlMetadata: UrlMetadataResponse = rootGetters['routing/urlMetadata'];
		const queryParams = rootGetters['routing/queryParams'];
		const str = Object.keys(queryParams)
			.filter((prop: any) => queryParams.hasOwnProperty(prop))
			.map((prop: any) => `${encodeURIComponent(prop)}=${encodeURIComponent(queryParams[prop])}`);

		const app_version = rootState.app.version;
		const app_environment = rootState.app.platform;

		const pulls: PullNotification[] = await PullAPIHelper.getAvailablePulls(rootState.user.jwId, {
			full_path: `${urlMetadata.full_path}?${str.join('&')}`,
			locale: urlMetadata.locale,
			object_type: urlMetadata.object_type,
			object_id: urlMetadata.object_id,
			show_id: urlMetadata.show_id,
			app_version,
			app_environment,
		});

		commit('SET_PULLS', pulls);
	},
};

// MUTATIONS
const mutations: MutationTree<State> = {
	SET_PULLS: (state, payload: PullNotification[]) => {
		state.pulls = payload;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<State, any>;
